import { PhotoSize } from '../contexts/syncing/Photos/PhotoGalleryContext';
import { pathToFileUri } from '../lib/fileUri';
import { ConnectionInfo, DirectoryContents, FileInfo, FileSystemInterface, FileSystemStorageInfo } from './FileSystemInterface';

const DEFAULT_STORAGE_SPACE = 8 * 1024 * 1024 * 1024;

const getParentDirectory = (filePath: string) => {
  const normalized = filePath.replace(/\/+$/, '');
  const lastSlash = normalized.lastIndexOf('/');
  return lastSlash <= 0 ? '/' : normalized.substring(0, lastSlash);
};

const getBaseName = (path: string) => {
  const normalized = path.replace(/\/+$/, '');
  return normalized.substring(normalized.lastIndexOf('/') + 1);
};

// Simple in-memory file system implementation for testing
export class InMemoryFileSystem implements FileSystemInterface {
  private files = new Map<string, Blob>();
  private fileModTimes = new Map<string, number>();
  /** Each directory maps to the set of its direct children (file and directory names) */
  private directories = new Map<string, Set<string>>();
  public documentDirectory = '/test/documents/';
  public cacheDirectory = '/test/cache/';

  constructor() {
    this.initDefaultDirectories();
  }

  private initDefaultDirectories() {
    this.makeDirectorySync('/test/documents');
    this.makeDirectorySync('/test/cache');
  }

  private makeDirectorySync(path: string) {
    const parts = path.replace(/\/+$/, '').split('/').filter(Boolean);
    let current = '';
    for (const part of parts) {
      // Ensure parent has a children set and registers this child
      const parent = current || '/';
      if (!this.directories.has(parent)) this.directories.set(parent, new Set());
      this.directories.get(parent)!.add(part);

      current += '/' + part;
      if (!this.directories.has(current)) this.directories.set(current, new Set());
    }
  }

  private normalizeDir(path: string): string {
    return path.replace(/\/+$/, '') || '/';
  }

  private hasDirectory(path: string): boolean {
    const normalized = this.normalizeDir(path);
    return this.directories.has(normalized);
  }

  private assertDirectoryExists(directory: string) {
    if (!this.hasDirectory(directory)) throw new Error(`Directory not found: ${directory}`);
  }

  private assertParentDirectoryExists(filePath: string) {
    this.assertDirectoryExists(getParentDirectory(filePath));
  }

  private addChildToParent(path: string) {
    const parent = getParentDirectory(path);
    const name = getBaseName(path);
    this.directories.get(this.normalizeDir(parent))?.add(name);
  }

  private removeChildFromParent(path: string) {
    const parent = getParentDirectory(path);
    const name = getBaseName(path);
    this.directories.get(this.normalizeDir(parent))?.delete(name);
  }

  private getFileWithAssert(file: string) {
    this.assertParentDirectoryExists(file);
    const fileContent = this.files.get(file);
    if (!fileContent) throw new Error(`File not found: ${file}`);
    return fileContent;
  }

  async getInfoAsync(path: string): Promise<{ exists: boolean }> {
    return { exists: this.files.has(path) || this.hasDirectory(path) };
  }

  async getFileInfo(path: string): Promise<FileInfo> {
    return {
      exists: this.files.has(path) || this.hasDirectory(path),
      size: this.files.get(path)?.size,
      modificationTime: this.fileModTimes.get(path),
      uri: path,
    };
  }

  async makeDirectoryAsync(path: string, options?: { intermediates: boolean }): Promise<void> {
    const normalized = this.normalizeDir(path);
    if (options?.intermediates) {
      this.makeDirectorySync(normalized);
    } else {
      const parent = getParentDirectory(normalized);
      this.assertDirectoryExists(parent);
      if (!this.directories.has(normalized)) {
        this.directories.set(normalized, new Set());
        this.addChildToParent(normalized);
      }
    }
  }

  async readAsBlobAsync(path: string): Promise<Blob> {
    return new Blob([this.getFileWithAssert(path)]);
  }

  async writeAsBlobAsync(path: string, content: Blob): Promise<void> {
    this.assertParentDirectoryExists(path);
    const isNew = !this.files.has(path);
    this.files.set(path, content);
    this.fileModTimes.set(path, Date.now());
    if (isNew) this.addChildToParent(path);
  }

  async deleteAsync(path: string): Promise<void> {
    if (this.files.has(path)) {
      this.files.delete(path);
      this.fileModTimes.delete(path);
      this.removeChildFromParent(path);
    } else if (this.hasDirectory(path)) {
      const normalized = this.normalizeDir(path);
      this.directories.delete(normalized);
      this.removeChildFromParent(normalized);
    }
  }

  async moveAsync(options: { from: string; to: string }): Promise<void> {
    const { from, to } = options;
    const data = this.getFileWithAssert(from);
    this.assertParentDirectoryExists(to);
    this.files.set(to, data);
    this.fileModTimes.set(to, this.fileModTimes.get(from) ?? Date.now());
    this.addChildToParent(to);
    this.files.delete(from);
    this.fileModTimes.delete(from);
    this.removeChildFromParent(from);
  }

  async uploadPhoto(
    filePath: string,
    scanId: string,
    photoId: string,
    connectionInfo: ConnectionInfo,
    timeoutSeconds?: number,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    throw new Error('Not implemented');
  }

  async downloadPhoto(photoId: string, filePath: string, size: PhotoSize, connectionInfo: ConnectionInfo): Promise<string> {
    throw new Error('Not implemented');
  }

  async scaleImageFile(loadFromPath: string, saveToPath: string, size: number): Promise<string> {
    const data = this.getFileWithAssert(loadFromPath);
    this.assertParentDirectoryExists(saveToPath);
    const isNew = !this.files.has(saveToPath);
    this.files.set(saveToPath, data);
    this.fileModTimes.set(saveToPath, Date.now());
    if (isNew) this.addChildToParent(saveToPath);
    return saveToPath;
  }

  async readDirectoryAsync(path: string): Promise<string[]> {
    const normalized = this.normalizeDir(path);
    this.assertDirectoryExists(normalized);
    return [...(this.directories.get(normalized) ?? [])];
  }

  async deleteDirectoryAsync(path: string): Promise<void> {
    const normalized = this.normalizeDir(path);
    if (!this.directories.has(normalized)) return;

    // Recursively delete children
    for (const child of [...(this.directories.get(normalized) ?? [])]) {
      const childPath = `${normalized}/${child}`;
      if (this.directories.has(childPath)) {
        await this.deleteDirectoryAsync(childPath);
      } else {
        this.files.delete(childPath);
        this.fileModTimes.delete(childPath);
      }
    }

    this.directories.delete(normalized);
    this.removeChildFromParent(normalized);
  }

  /** Sum the byte size of all files directly in a directory (recursive). */
  async getDirectorySize(path: string): Promise<number> {
    const normalized = this.normalizeDir(path);
    if (!this.directories.has(normalized)) return 0;
    let total = 0;

    // files
    for (const child of this.directories.get(normalized)!) {
      const childPath = `${normalized}/${child}`;
      const info = await this.getFileInfo(childPath);
      if (info.size) total += info.size;
    }

    const directory = this.directories.get(normalized);
    if (directory) {
      for (const child of directory) {
        total += await this.getDirectorySize(`${normalized}/${child}`);
      }
    }
    return total;
  }

  async getPhotoGalleryStorageInfo(): Promise<FileSystemStorageInfo> {
    const totalStorageSpace = DEFAULT_STORAGE_SPACE;
    let used = 0;
    for (const file of this.files.values()) {
      used += file.size ?? 0;
    }
    return { freeStorageSpace: totalStorageSpace - used, totalStorageSpace };
  }

  /** Set a file's modificationTime (for testing eviction ordering) */
  setModificationTime(path: string, time: number): void {
    if (!this.files.has(path)) throw new Error(`File not found: ${path}`);
    this.fileModTimes.set(path, time);
  }

  clear(): void {
    this.files.clear();
    this.fileModTimes.clear();
    this.directories.clear();
    // Root directory
    this.directories.set('/', new Set());
    this.initDefaultDirectories();
  }

  async getFileExists(path: string): Promise<boolean> {
    return this.files.has(path);
  }

  async getDirectoryExists(path: string): Promise<boolean> {
    return this.directories.has(this.normalizeDir(path));
  }

  pathToUri(path: string): string {
    return pathToFileUri(path);
  }

  async getDirectoryContents(path: string): Promise<DirectoryContents> {
    const normalized = this.normalizeDir(path);
    const filesAndDirectories = this.directories.get(normalized);
    if (!filesAndDirectories) return { files: [], directories: [] };
    const directories: string[] = [];
    const files: string[] = [];
    for (const item of filesAndDirectories) {
      // if has extension, its a file
      if (item.includes('.')) {
        files.push(item);
      } else {
        directories.push(item);
      }
    }
    return { files, directories };
  }
}
